import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from './Request';

interface UserData {
  id: string;
  name: string;
  email: string;
  permissionsActives?: any[];
  userClient?: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  login: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Si el interceptor detecta una sesión no recuperable (refresh falló),
  // limpiamos el estado de auth para que las rutas protegidas redirijan al
  // login y no se genere un bucle de rebote (dashboard <-> login).
  useEffect(() => {
    const onSessionExpired = () => {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    };
    window.addEventListener('dylo:session-expired', onSessionExpired);
    return () => window.removeEventListener('dylo:session-expired', onSessionExpired);
  }, []);

  /**
   * 🔑 **Al renovarse el token, se vuelven a leer los permisos.**
   *
   * `permissionsActives` se capturaba al iniciar sesión y solo se refrescaba al montar este
   * provider, o sea en una **recarga completa** de la página. Navegar entre pantallas no lo
   * remonta, así que en una jornada larga la copia se quedaba congelada.
   *
   * Eso produjo un reporte real: a un usuario le dieron `LD_EDIT` con la sesión abierta. El
   * **backend** lo aceptó al instante —`authorize()` consulta la base en cada petición— pero
   * el **frontend** siguió escondiendo el selector de estatus, porque decide con esta copia.
   * Se leyó como "no puedo cambiar el estatus" y se resolvió al volver a entrar.
   *
   * 📌 Se escucha el refresco del token y no un temporizador propio porque el token vive 15
   * minutos: quien está trabajando ya pasa por ahí solo. Así lo rancio queda acotado a esos
   * 15 minutos sin agregar peticiones periódicas para las pestañas inactivas.
   *
   * ⚠️ Si esta llamada falla, **no se toca la sesión**: un fallo de red aquí no significa que
   * el usuario perdió acceso, y limpiar el estado lo mandaría al login sin motivo. Se
   * conservan los permisos que ya se tenían.
   */
  useEffect(() => {
    /**
     * 🔴 Candado de reentrada, y hace falta de verdad.
     *
     * Este manejador pide `/auth/me` con la instancia que **tiene el interceptor**. Si esa
     * petición contestara 401, el interceptor renovaría el token y volvería a emitir
     * `session-refreshed`, que dispararía otro `/auth/me`… un bucle infinito de peticiones.
     *
     * No es descabellado: basta un desfase de reloj o una cookie que el navegador no mandó.
     * Con el candado, mientras una consulta esté en curso las demás señales se ignoran, así
     * que el ciclo se corta en la primera vuelta.
     */
    let inFlight = false;

    const onSessionRefreshed = () => {
      if (inFlight) return;
      inFlight = true;
      axiosInstance
        .get('/auth/me')
        .then((res) => setUser(res.data.user))
        .catch(() => {
          /* se conserva la copia anterior a propósito */
        })
        .finally(() => {
          inFlight = false;
        });
    };
    window.addEventListener('dylo:session-refreshed', onSessionRefreshed);
    return () => window.removeEventListener('dylo:session-refreshed', onSessionRefreshed);
  }, []);

  // Verificar autenticación al cargar (las cookies se envían automáticamente)
  useEffect(() => {
    axiosInstance.get('/auth/me')
      .then((res) => {
        setUser(res.data.user);
        setIsAuthenticated(true);
      })
      .catch(() => {
        setIsAuthenticated(false);
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = async (email: string, password: string) => {
    const res = await axiosInstance.post('/auth/login', { email, password });
    setUser(res.data.user);
    setIsAuthenticated(true);
    setIsLoading(false);
    return res.data;
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch {
      // Even if the logout call fails, clear local state
    }
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
